/**
 * KlachtView — read-only overlay met details van een open klacht.
 * Geopend vanuit KlachtBanner. Toont status, beschrijving, vervolg-WO's
 * en een tijdlijn van klant-relevante updates.
 *
 * Brand voice: "uw" (formele klant-relatie). Warm, vakkundig, transparant.
 * Geen actie-knoppen — klant kan niet zelf een klacht heropenen of muteren.
 * Wel een footer met contact-route via service@kleda.nl.
 */

import { type ClientKlacht, KLACHT_STATUS_LABELS, klachtTypeLabel } from '../lib/klacht-types'

interface Props {
  klacht: ClientKlacht
  onClose: () => void
}

export function KlachtView({ klacht, onClose }: Props) {
  const isOpgelost = klacht.status === 'Opgelost'
  const isGesloten = klacht.status === 'Gesloten'
  const statusBg =
    isOpgelost ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
    isGesloten ? 'bg-gray-100 text-gray-700 border-gray-300' :
                 'bg-red-100 text-red-800 border-red-300'

  const datumGeopendStr = formatDatum(klacht.datumGeopend)

  // Filter alleen klant-relevante updates (StatusChange + Comment).
  // Interne ActionAdded/SystemNote krijgen klanten niet te zien.
  const klantRelevanteUpdates = (klacht.laatsteUpdates || []).filter(
    (u) => u.type === 'StatusChange' || u.type === 'Comment'
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 overflow-y-auto"
      role="dialog"
      aria-labelledby="klacht-view-title"
    >
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />

      <div className="relative w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col my-0 sm:my-md mx-0 sm:mx-md">
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-2xl border-b border-hairline px-md py-md z-10">
          <div className="flex items-start justify-between gap-md">
            <div className="min-w-0">
              <p className="text-body-sm text-ink-muted">{klacht.klachtNummer}</p>
              <h2 id="klacht-view-title" className="text-heading-sub font-bold text-ink">
                Status van uw klacht
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-sm -mr-sm text-ink-muted hover:text-ink"
              aria-label="Sluiten"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-md py-md space-y-lg">
          {/* Status pill */}
          <div className={`inline-flex items-center gap-sm px-md py-sm rounded-full border ${statusBg}`}>
            <span className="text-body font-semibold">
              {KLACHT_STATUS_LABELS[klacht.status]}
            </span>
          </div>

          {/* Key facts */}
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-md text-body-sm">
            <div>
              <dt className="text-ink-muted">Type</dt>
              <dd className="text-ink font-medium">{klachtTypeLabel(klacht.type)}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">Geopend</dt>
              <dd className="text-ink font-medium">{datumGeopendStr}</dd>
            </div>
          </dl>

          {/* Beschrijving */}
          {klacht.beschrijving && (
            <div>
              <h3 className="text-body font-semibold text-ink mb-xs">Wat is er aan de hand</h3>
              <p className="text-body text-ink-muted whitespace-pre-line">{klacht.beschrijving}</p>
            </div>
          )}

          {/* Vervolg-WO's */}
          {klacht.vervolgWerkOrders && klacht.vervolgWerkOrders.length > 0 && (
            <div>
              <h3 className="text-body font-semibold text-ink mb-sm">Vervolgopdrachten</h3>
              <ul className="space-y-sm">
                {klacht.vervolgWerkOrders.map((wo) => (
                  <li
                    key={wo.werkOrderId}
                    className="rounded-kleda-md border border-border-subtle p-md"
                  >
                    <p className="text-body font-medium text-ink">
                      Vervolg op uw klacht
                    </p>
                    <p className="text-body-sm text-ink-muted">
                      {wo.werkOrderNummer} — {wo.status}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tijdlijn */}
          {klantRelevanteUpdates.length > 0 && (
            <div>
              <h3 className="text-body font-semibold text-ink mb-sm">Wat er gebeurd is</h3>
              <ul className="space-y-md">
                {klantRelevanteUpdates.map((update, idx) => (
                  <li key={idx} className="flex gap-md">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary mt-1.5" />
                      {idx < klantRelevanteUpdates.length - 1 && (
                        <div className="w-px flex-1 bg-border-subtle mt-xs" />
                      )}
                    </div>
                    <div className="pb-md min-w-0">
                      <p className="text-body-sm text-ink-muted">{formatDatum(update.datum)}</p>
                      <p className="text-body text-ink">{update.tekst}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer — contact */}
        <div className="sticky bottom-0 bg-white border-t border-hairline px-md py-md">
          <p className="text-body-sm text-ink-muted">
            Heeft u een vraag over uw klacht? Mail{' '}
            <a href="mailto:service@kleda.nl" className="text-primary underline">
              service@kleda.nl
            </a>
            {' '}— we reageren binnen één werkdag.
          </p>
        </div>
      </div>
    </div>
  )
}

function formatDatum(iso: string | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}
